import {
  UserSettings,
  DateFormatSettings,
  LanguageSettings,
  MeasurementSystemSettings,
  ThemeSettings,
} from '../models/settings.model';

declare const window: any;

export class SettingsUtil {
  static isLatam(countryID: number): boolean {
    return countryID === 46; // user is from brazil, extend for other latam countries
  }

  /**
   * Get date format settings by ID
   * @param dateFormats - Array of date format settings
   * @param dateFormatID - Date format identifier
   * @returns Date format settings or undefined
   */
  static getDateFormat(
    dateFormats: DateFormatSettings[] | undefined,
    dateFormatID: number | undefined
  ): DateFormatSettings | undefined {
    return dateFormats?.find((item) => item.id === dateFormatID);
  }

  /**
   * Get language settings by ID
   * Updates Adobe Data Layer if available
   * @param languages - Array of language settings
   * @param languageID - Language identifier
   * @returns Language settings or undefined
   */
  static getLanguage(
    languages: LanguageSettings[] | undefined,
    languageID: number | undefined
  ): LanguageSettings | undefined {
    const languageApply = languages?.find((item) => item.id === languageID);

    // Update Adobe Data Layer if available
    if (window.adobeDataLayer?.[0] && languageApply) {
      window.adobeDataLayer[0]._henkel = window.adobeDataLayer[0]._henkel || {
        page: { language: '' },
      };
      window.adobeDataLayer[0]._henkel.page.language = languageApply.abbreviation;
    }

    return languageApply;
  }

  /**
   * Get measurement system settings by ID
   * @param measurementSystems - Array of measurement system settings
   * @param measurementSystemID - Measurement system identifier
   * @returns Measurement system settings or undefined
   */
  static getMeasurementSystem(
    measurementSystems: MeasurementSystemSettings[] | undefined,
    measurementSystemID: number | undefined
  ): MeasurementSystemSettings | undefined {
    return measurementSystems?.find((item) => item.id === measurementSystemID);
  }

  /**
   * Get theme settings by ID
   * @param themes - Array of theme settings
   * @param themeID - Theme identifier
   * @returns Theme settings or undefined
   */
  static getTheme(
    themes: ThemeSettings[] | undefined,
    themeID: number | undefined
  ): ThemeSettings | undefined {
    return themes?.find((item) => item.id === themeID);
  }

  /**
   * Get all computed settings for a user
   * @param settings - User settings object
   * @returns Object containing computed values
   */
  static getComputedSettings(settings: UserSettings) {
    return {
      isLatam: SettingsUtil.isLatam(settings.countryID ?? 0),
      dateFormat: SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID),
      language: SettingsUtil.getLanguage(settings.languages, settings.languageID),
      measurementSystem: SettingsUtil.getMeasurementSystem(
        settings.measurementSystems,
        settings.measurementSystemID
      ),
      theme: SettingsUtil.getTheme(settings.themes, settings.themeID),
    };
  }
}
